import axios from 'axios';
import { displayToast } from './Toast';

const multipartForm = 'multipart/form-data';

const apiService = async ({
  url,
  method,
  data,
  onSuccess,
  onError,
  onFinally,
  contentType = 'application/json'
}) => {
  // Generic API call function for unauthenticated endpoints
  const response = await axios({
    method,
    baseURL: '/',
    url,
    params: method === 'GET' ? data || {} : {},
    data: method !== 'GET' ? data || {} : {},
    headers: {
      'Content-Type': contentType,
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    timeout: 90000
  })
    .then((receivedResponse) => {
      onSuccess?.();
      return receivedResponse;
    })
    .catch((error) => {
      return onError ? onError(error) : displayToast(error.message, 'error');
    })
    .finally(() => {
      onFinally?.();
    });
  return response?.data ?? null;
};

export const deleteProposal = (url) => {
  return apiService({
    url,
    method: 'DELETE'
  });
};

export const shortlistProposal = (url) => {
  return apiService({
    url,
    method: 'POST'
  });
};

export const unShortlistProposal = (url) => {
  return apiService({
    url,
    method: 'DELETE'
  });
};

export const fetchConnectionsReport = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params
  });
};

export const fetchConnectionSummary = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchDashoardData = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchFundingTypes = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchIndustries = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchInvestors = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const fetchLanguages = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const fetchMessageById = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchMessages = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const fetchNewsfeed = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchNotifications = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const fetchPlaces = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const fetchProposalDetails = (url) => {
  return apiService({
    url,
    method: 'GET',
    onError: (error) => {
      // Re-throw so the caller can distinguish success from failure.
      // apiService would otherwise swallow the error, show its own toast,
      // and resolve null after displayToast's 3-second timer.
      throw error;
    }
  });
};

export const fetchProposals = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const fetchQuickLinks = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchStages = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchTaxReliefs = (url) => {
  return apiService({
    url,
    method: 'GET'
  });
};

export const fetchUsers = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params
  });
};

export const generateHtmlTemplate = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const messageAction = (url) => {
  return apiService({
    url,
    method: 'POST'
  });
};

export const newsletterGetAction = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const newsletterPostAction = async (url, params) => {
  const response = await axios({
    method: 'POST',
    url,
    data: params
  });
  return response.data;
};

export const nudgeInvestor = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params
  });
};

export const postNewsfeed = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm
  });
};

export const publishProposal = async (url) => {
  // These endpoints return 204 No Content, so the (empty) body can't signal
  // success. apiService returns null only on error, so treat anything else as
  // a success.
  const result = await apiService({
    url,
    method: 'POST'
  });
  return result !== null;
};

export const rateInvestor = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params
  });
};

export const removeNewsfeedImage = (url, params) => {
  return apiService({
    url,
    method: 'DELETE',
    data: params,
    contentType: multipartForm
  });
};

export const sendGetMoreRequestAboutBrokingProposal = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm
  });
};

export const sendMessage = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm
  });
};

export const shortlistInvestor = (url) => {
  return apiService({
    url,
    method: 'POST'
  });
};

export const takeProposalOffline = async (url) => {
  // 204 No Content: null means error, anything else means success.
  const result = await apiService({
    url,
    method: 'POST'
  });
  return result !== null;
};

export const withdrawProposalFromApproval = async (url) => {
  // 204 No Content: null means error, anything else means success.
  const result = await apiService({
    url,
    method: 'DELETE'
  });
  return result !== null;
};

export const unShortlistInvestor = (url) => {
  return apiService({
    url,
    method: 'DELETE'
  });
};

export const postBenefitOffer = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm
  });
};

export const postCaseStudy = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm
  });
};

export const postBlogArticle = (url, params, onSuccess, onError, onFinally) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm,
    onSuccess,
    onError,
    onFinally
  });
};

export const fetchBlogArticles = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params,
    contentType: 'application/x-www-form-urlencoded'
  });
};

export const changeBlogArticleStatus = (url, onSuccess, onError) => {
  return apiService({
    url,
    method: 'POST',
    onSuccess,
    onError
  });
};

export const fetchBlogTags = (url, params) => {
  return apiService({
    url,
    method: 'GET',
    data: params
  });
};

export const postLearnArticle = (url, params) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    contentType: multipartForm
  });
};

export const proposalShareRegister = (url) => {
  return apiService({
    url,
    method: 'POST'
  });
};

export const removeBenefitOfferImage = (url, params) => {
  return apiService({
    url,
    method: 'DELETE',
    data: params
  });
};

export const submitCancellationSurvey = (
  url,
  params,
  onSuccess,
  onError,
  onFinally
) => {
  return apiService({
    url,
    method: 'POST',
    data: params,
    onSuccess,
    onError,
    onFinally
  });
};
